import type { Event } from "@codesandbox/pitcher-common";
import { Terminal as XTerminal } from "@xterm/xterm";
import { Box, useThemeTokens } from "prism-react";
import { useEffect, useRef } from "react";

export type LoadProgressTerminalProps = {
  onOutput: Event<string>;
};

export const LoadProgressTerminal: React.FC<LoadProgressTerminalProps> = ({
  onOutput,
}) => {
  const terminalElement = useRef<HTMLDivElement>(null);
  const theme = useThemeTokens();

  useEffect(() => {
    if (!terminalElement.current) {
      return;
    }

    const terminal = new XTerminal({
      fontFamily:
        "FiraCode, SFMono-Regular, Consolas, 'Liberation Mono', Menlo, Courier, monospace",
      theme: {
        background: theme.colors["neutral-bg-low"],
        foreground: theme.colors["neutral-fg-high"],
        cursor: theme.colors["neutral-fg-high"],
        cursorAccent: theme.colors["neutral-fg-high"],
        brightGreen: "#85CEA8",
        brightMagenta: theme.colors["accent-secondary-base"],
        magenta: "#7660B9",
        green: "#319382",
        yellow: theme.colors["informative-warning-base"],
        brightYellow: theme.colors["informative-warning-base"],
        red: theme.colors["informative-error-base"],
        // Not red and cyan but style the colors used in the prompt header.
        brightRed: "#808080",
        brightCyan: theme.colors["accent-primary-base"],
      },
      fontSize: 13,
      lineHeight: 1.3,
    });

    terminal.open(terminalElement.current);

    import("@xterm/addon-fit").then(({ FitAddon }) => {
      const addon = new FitAddon();
      addon.activate(terminal);
      addon.fit();
    });

    const disposer = onOutput((data) => {
      terminal.write(data);
    });

    return () => {
      disposer.dispose();
      terminal.dispose();
    };
  }, [terminalElement, onOutput, theme.colors]);

  return (
    <div style={{ height: "100%", width: "100%" }}>
      <Box
        ref={terminalElement}
        css={{
          borderRadius: "$3",
          backgroundColor: "$neutral-bg-low",
          border: "1px solid $neutral-bg-subtle",
          zIndex: 100,
          textAlign: "left",
          padding: "$4",
          height: "30vh",
          width: "100%",
          overflow: "hidden",
        }}
        selectable
      />
    </div>
  );
};
